import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { CheckoutService } from './services/checkout.service';
import { PAYMENTS_CONFIG } from './payments.config';
import { EFeeType, PaymentMethod } from './models/payment.enums';
import {
  PaymentConfig,
  PersonalInfo,
  SummaryData,
} from './models/payment.models';
import { Currency } from '@dlap-ui/shared-forms/models/currency';
import { Auth } from './components/auth/auth';
import { Summary } from './components/summary/summary';
import { Mastercard } from './components/mastercard/mastercard';
import { EcocashComponent } from './components/ecocash/ecocash';
import { Zimswitch } from './components/zimswitch/zimswitch';
import { ShieldCheckIcon } from '../ui-icons/icons';

@Component({
  selector: 'lib-payments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
    NzSelectModule,
    NzSpinModule,
    Auth,
    Summary,
    Mastercard,
    EcocashComponent,
    Zimswitch,
    ShieldCheckIcon,
  ],
  templateUrl: './payments.html',
  styleUrl: './payments.css',
})
export class Payments implements OnInit {
  private readonly checkoutService = inject(CheckoutService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly config = inject(PAYMENTS_CONFIG);
  private readonly message = inject(NzMessageService);

  readonly isDeeds = this.config.appName === 'Deeds';

  selectedPaymentSection: PaymentMethod = null;
  checkoutInfo: any = null;
  paymentConfig: PaymentConfig | null = null;
  referenceNumber!: string;
  iveriAppId = '';
  isLoading = true;
  callbackUrl!: string;
  feeId!: string;
  selectedFeeType: any;
  feeType!: EFeeType | null;
  availableCurrencies: any[] = [];
  selectedCurrencyId: string | null = null;
  user: any = null;
  topUpForOtherUser = false;
  personalInfo: PersonalInfo | null = null;
  isManualInput = false;
  savedUser: any = null;

  summaryData: SummaryData = {
    serviceType: null,
    serviceName: null,
    amount: 0.0,
    total: 0,
    currency: { symbol: 'USD', name: 'USD' },
    tax: 0,
  };

  ngOnInit(): void {
    const rawUser = localStorage.getItem(this.config.userStorageKey ?? 'auth_user');
    this.user = rawUser ? JSON.parse(rawUser) : null;

    if (this.isDeeds) {
      this.getApplicationId();
    }

    this.route.params.subscribe((params: any) => {
      this.feeType = params['feeType']?.toUpperCase() || null;

      if (this.user) {
        this.savedUser = this.user;
        const { email, firstName, lastName, nationalId, phoneNumber } = this.savedUser;
        this.personalInfo = {
          email,
          phoneNumber,
          identificationNumber: nationalId,
          firstname: firstName,
          lastname: lastName,
        };
      }

      if (params['userId']) {
        this.user = { id: params['userId'] };
        this.topUpForOtherUser = true;
      }

      this.initFeeTypes();
    });

    this.route.queryParams.subscribe((queryParams: any) => {
      this.referenceNumber = queryParams?.reference;
      this.feeId = queryParams?.reference;

      if (queryParams?.amount) {
        this.summaryData.amount = +queryParams.amount;
        this.summaryData.total = +queryParams.amount;
      }
      if (queryParams?.packageName) {
        this.summaryData.serviceName = queryParams.packageName;
      }
      if (this.referenceNumber && this.feeId) {
        this.getReferenceInfo(this.referenceNumber);
        this.router.navigate([], {
          queryParams: { reference: this.referenceNumber, feeId: this.feeId },
          queryParamsHandling: 'merge',
        });
      }
      this.callbackUrl =
        queryParams?.returnUrl ?? this.config.frontendUrl ?? window.location.origin + '/';
    });
  }

  private getApplicationId(): void {
    this.checkoutService.getApplicationId().subscribe((res) => {
      this.iveriAppId = res;
    });
  }

  get showResultModal(): boolean {
    return (
      this.checkoutInfo?.transactionStatus === 'COMPLETE' ||
      this.checkoutInfo?.transactionStatus === 'FAILED'
    );
  }

  togglePaymentOption(name: PaymentMethod): void {
    if (this.summaryData.amount <= 0) {
      this.message.error('Enter amount first!');
      return;
    }

    if (this.selectedPaymentSection === name) {
      this.selectedPaymentSection = null;
      return;
    }
    this.selectedPaymentSection = name;

    const base: PaymentConfig = { ...this.paymentConfig };

    if (name === 'MASTERCARD') {
      this.paymentConfig = {
        ...base,
        gateway: this.config.iveriGateway,
        ReturnUrl: this.config.iveriReturnUrl,
        ApplicationID: this.isDeeds ? this.iveriAppId : this.config.iveriApplicationId,
        MerchantReference: this.referenceNumber,
        Amount: this.summaryData.amount,
        Currency: this.currencyCode(this.summaryData.currency),
        callbackUrl: this.callbackUrl,
        PAN: '',
        ExpiryDate: '',
        paymentChannel: this.isDeeds ? 'CBZ' : 'IVERI',
      };
    } else if (name === 'ZIMSWITCH') {
      this.paymentConfig = this.user?.id
        ? {
            ...base,
            feeId: this.feeId,
            userId: this.user.id,
            amount: this.summaryData.amount,
            merchantReference: this.referenceNumber,
            callbackUrl: this.callbackUrl,
            paymentChannel: this.isDeeds ? 'ZIM_SWITCH' : 'ZIMSWITCH',
          }
        : {
            ...base,
            feeId: this.feeId,
            merchantReference: this.referenceNumber,
            amount: this.summaryData.amount,
            ...(this.personalInfo ?? {}),
          };
    } else if (name === 'ECOCASH') {
      this.paymentConfig = {
        ...base,
        gateway: this.config.iveriGateway,
        ReturnUrl: this.config.iveriReturnUrl,
        ApplicationID: this.isDeeds ? this.iveriAppId : this.config.iveriApplicationId,
        MerchantReference: this.referenceNumber,
        Amount: this.summaryData.amount,
        Currency: this.currencyCode(this.summaryData.currency),
        callbackUrl: this.callbackUrl,
        paymentChannel: 'ECOCASH',
      };
    }
  }

  private initFeeTypes(): void {
    this.checkoutService.getPublicActiveFeeTypes().subscribe({
      error: () => {
        this.isLoading = false;
        this.message.error('Failed to load payment options. Please try again.');
      },
      next: (results) => {
      this.selectedFeeType = this.feeType
        ? results.find(
            (el: any) =>
              el.feeType !== 'LOAD-WALLET' &&
              (el.feeType === this.feeType || el.feeType === EFeeType.WALLET_FEE)
          )
        : results.find((el: any) => el.feeType === EFeeType.WALLET_FEE);

      if (!this.selectedFeeType) {
        this.isLoading = false;
        return;
      }

      this.availableCurrencies = results
        .filter((r: any) => r.feeType === this.selectedFeeType.feeType)
        .map((m: any) => ({ ...m, currency: m.currency as Currency, currencyId: m.currency.id }));

      if (this.selectedFeeType.feeType === EFeeType.WALLET_FEE) {
        this.isManualInput = true;
        this.summaryData.serviceName = 'Top up amount';
        this.isLoading = false;
        this.paymentConfig = { isWallet: true, feeId: this.selectedFeeType.id };
      }

      this.toggleFeeType(this.selectedFeeType);

      if ((this.user || this.savedUser) && !this.isManualInput) {
        if ((this.topUpForOtherUser || this.savedUser) && this.config.appPortal !== 'Repz') {
          this.generateReferenceWithoutFee(this.selectedFeeType.id);
        } else {
          this.isLoading = false;
          this.feeId = this.selectedFeeType.id;
        }
      } else {
        this.isLoading = false;
        this.feeId = this.selectedFeeType.id;
      }
      },
    });
  }

  private getReferenceInfo(reference: string): void {
    this.checkoutService.getReference(reference).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.checkoutInfo = res;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private toggleFeeType(feeType: any): void {
    if (!feeType) return;
    this.summaryData.serviceType = feeType.feeType;
    this.summaryData.serviceName = feeType.name;
    this.summaryData.currency = feeType.currency ?? this.summaryData.currency;
    this.summaryData.amount = this.summaryData.amount > 0 ? this.summaryData.amount : feeType.price;
    this.summaryData.total = this.summaryData.amount;
  }

  selectCurrency(currencyId: string): void {
    const currency = this.availableCurrencies.find((c) => c.currencyId === currencyId);
    if (!currency) return;
    this.selectedCurrencyId = currencyId;
    this.toggleFeeType(currency);
    this.summaryData.currency = currency.currency;
    if (this.paymentConfig) {
      this.paymentConfig.Currency = this.currencyCode(currency.currency);
    }
  }

  currencyCode(currency: Currency | null | undefined): string {
    return currency?.symbol || currency?.name || 'USD';
  }

  private generateReferenceWithoutFee(feeId: string | null = null, amount = 0): void {
    this.feeId = feeId as string;
    let payload: any = {};

    if (this.user) {
      payload.userId = this.user.id;
      payload.feeId = feeId;
      payload.callbackUrl = this.callbackUrl;
      if (amount > 0) payload.amount = amount;
    } else {
      payload = {
        ...(feeId && { feeId }),
        callbackUrl: this.callbackUrl,
        ...(this.personalInfo ?? {}),
      };
    }

    this.checkoutService.generateMerchantReferenceWithoutFee(payload).subscribe((res: any) => {
      this.isLoading = false;
      this.referenceNumber = res.invoiceNumber;
      this.checkoutInfo = { ...res, feeId: payload.feeId, callbackUrl: this.callbackUrl };
    });
  }

  onPersonalInfo(evt: PersonalInfo): void {
    this.personalInfo = evt;
    this.generateReferenceWithoutFee(this.selectedFeeType?.id);
  }

  onPriceChange(amount: number): void {
    this.paymentConfig = { ...this.paymentConfig, Amount: amount, amount };
    this.summaryData.amount = amount;
  }

  onPaymentSubmitted(): void {
    this.close();
  }

  tryAgain(): void {
    if (this.checkoutInfo) this.checkoutInfo.transactionStatus = null;
  }

  close(): void {
    window.close();
  }

  goBack(): void {
    this.location.back();
  }
}
